import { Card } from "@devhop/ui";
import { memo } from "react";
import type { NodeProps } from "reactflow";
import { Handle, Position } from "reactflow";
import { AppImage } from "@/shared/ui/AppImage";
import type { PositionWithDetails } from "../../domain/types";

// Get level color based on position level with dark mode support
function getLevelColor(level: number): string {
  const colors: readonly string[] = [
    "bg-red-50 border-red-200 text-red-800 dark:bg-red-900 dark:border-red-800 dark:text-red-200", // CEO - Level 1
    "bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-900 dark:border-blue-800 dark:text-blue-200", // Director - Level 2
    "bg-green-50 border-green-200 text-green-800 dark:bg-green-900 dark:border-green-800 dark:text-green-200", // Manager - Level 3
    "bg-yellow-50 border-yellow-200 text-yellow-800 dark:bg-yellow-900 dark:border-yellow-800 dark:text-yellow-200", // Staff - Level 4
    "bg-purple-50 border-purple-200 text-purple-800 dark:bg-purple-900 dark:border-purple-800 dark:text-purple-200", // Staff Level 2 - Level 5+
  ];

  const index = Math.min(level - 1, colors.length - 1);
  return colors[index]!;
}

export const OrgChartNodeComponent = memo(({ data }: NodeProps) => {
  const position: PositionWithDetails = data.position;

  const levelColor = getLevelColor(position.level);
  const employees = position.employees || [];
  const maxVisibleAvatars = 3;
  const visibleEmployees = employees.slice(0, maxVisibleAvatars);
  const remainingCount = employees.length - maxVisibleAvatars;

  return (
    <div className="relative">
      {/* Connection handles */}
      <Handle
        type="target"
        position={Position.Top}
        className="h-2 w-2 border border-white bg-gray-400"
      />

      <Card
        className={`w-40 cursor-pointer gap-0 border p-2 transition-colors hover:shadow-sm ${levelColor}`}
      >
        {/* Employee Avatars Stack */}
        {employees.length > 0 ? (
          <div className="mb-1 flex justify-center">
            <div className="relative flex items-center">
              {visibleEmployees.map((employee, index) => (
                <div
                  key={employee.id}
                  className="relative"
                  style={{
                    marginLeft: index > 0 ? "-8px" : "0",
                    zIndex: visibleEmployees.length - index,
                  }}
                >
                  <div className="size-10 overflow-hidden rounded-full border-2 border-background bg-muted">
                    {employee.image ? (
                      <AppImage
                        src={employee.image}
                        alt={employee.name}
                        className="size-full"
                        fit="cover"
                        showLoading={false}
                        fallback={
                          <div className="flex h-full w-full items-center justify-center bg-muted-foreground/20">
                            <span className="font-medium text-muted-foreground text-xs">
                              {employee.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        }
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-muted-foreground/20">
                        <span className="font-medium text-muted-foreground text-xs">
                          {employee.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {remainingCount > 0 && (
                <div
                  className="relative flex size-10 items-center justify-center rounded-full border-2 border-background bg-muted-foreground/30"
                  style={{
                    marginLeft: "-8px",
                    zIndex: 0,
                  }}
                >
                  <span className="font-medium text-muted-foreground text-xs">
                    +{remainingCount}
                  </span>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="mb-1 flex justify-center">
            <div className="size-10 overflow-hidden rounded-full bg-muted/50">
              <div className="flex h-full w-full items-center justify-center">
                <span className="text-muted-foreground text-xs">-</span>
              </div>
            </div>
          </div>
        )}

        {/* Position Title */}
        <h3 className="text-center font-medium text-sm leading-tight">
          {position.title}
        </h3>

        {/* Employee Status */}
        <div className="text-center text-muted-foreground text-xs">
          {employees.length > 0 ? (
            <span className="block truncate">
              {employees.length === 1
                ? employees[0]!.name
                : `${employees.length} ຄົນ`}
            </span>
          ) : (
            <span>ວ່າງ</span>
          )}
        </div>
      </Card>

      <Handle
        type="source"
        position={Position.Bottom}
        className="h-2 w-2 border border-white bg-gray-400"
      />
    </div>
  );
});

OrgChartNodeComponent.displayName = "OrgChartNode";
